import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'grab-ui';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import {
  SCRIBE_SHOPIFY_STATES,
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
} from 'applications/scribe/instrumentations/track';
import ShopifyTrackEventContext from 'views/v2/shopify-home/track-event';
import { getCompanyID } from 'helper/user';
import { updateShopifyConfigurationPickUpAddress } from 'actions/shopify-endpoints';
import { ShopContext } from 'views/v2/shopify-home/contexts/shop-context';
import useNotification, {
  makeErrorMessage,
} from 'views/v2/shopify-home/hooks/useNotification';

const DEFAULT_SHOP_ADDRESS_SETTINGS_MODAL_CONTEXT = {
  form: null,
  formId: '',
  userInfo: null,
  refetchShops: () => undefined,
  pickUpAddress: null,
  showModal: false,
  setShowModal: () => undefined,
  isFormLoading: false,
  setFormLoading: () => undefined,
  submitFormHandler: () => undefined,
  cancelFormHandler: () => undefined,
  formInitialValues: null,
};

export const ShopAddressSettingsModalContext = React.createContext(
  DEFAULT_SHOP_ADDRESS_SETTINGS_MODAL_CONTEXT,
);

export const FIELD_NAME = {
  CONTACT_PERSON: 'Contact Person',
  CONTACT_NUMBER: 'Contact Number',
  ADDRESS: 'Address',
  ADDRESS_DETAIL: 'Address Detail',
  LOCATION_NOTES: 'Location Notes',
};

const makeFormInitialValues = pickUpAddress => {
  const { shopifyLocation, grabPoi, metaData, verified } = pickUpAddress;
  const address = verified
    ? _get(grabPoi, 'address', '')
    : _get(shopifyLocation, 'address1', '');
  const keywords = verified
    ? _get(grabPoi, 'keywords', '')
    : _get(shopifyLocation, 'address1', '');
  const addressDetail = verified
    ? _get(metaData, 'addressDetail', '')
    : _get(shopifyLocation, 'address2', '');

  return {
    grabPoi: {
      ...grabPoi,
      address,
      keywords,
    },
    contactName: _get(metaData, 'contact.name', ''),
    contactPhone: _get(metaData, 'contact.phone', ''),
    addressDetail,
    locationNotes: _get(metaData, 'locationNotes', ''),
    verified,
  };
};

const ShopAddressSettingsModalContextProvider = ({
  children,
  dispatch,
  form,
  pickUpAddress,
}) => {
  const trackCtx = React.useContext(ShopifyTrackEventContext);
  const { shop, userInfo, refetchShops } = React.useContext(ShopContext);
  const { showNotification } = useNotification(dispatch);

  const companyID = getCompanyID(userInfo);

  const shopIdentifier = shop.shop;
  const shopifyLocationID = pickUpAddress.shopifyLocation.ID;
  const formId = `addressFrom-${shopifyLocationID}`;
  const defaultFormInitialValue = makeFormInitialValues(pickUpAddress);

  const [showModal, setShowModal] = React.useState(false);
  const [isFormLoading, setFormLoading] = React.useState(false);
  const [formInitialValues, setFormInitialValues] = React.useState(
    defaultFormInitialValue,
  );

  const fireSavePickUpAddressTrackEvent = (previousPoiID, newPoiID) => {
    trackCtx.fireShopifyTrackEvent({
      stateName: SCRIBE_SHOPIFY_STATES.EXPRESS_SHOPIFY_SETTINGS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.SAVE_PICKUP_ADDRESS,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.PREVIOUS_PICKUP_POI_ID]: previousPoiID,
        [SCRIBE_SHOPIFY_PARAMS.PICKUP_POI_ID]: newPoiID,
      },
    });
  };

  const cancelFormHandler = () => {
    setFormInitialValues(defaultFormInitialValue);
    setFormLoading(false);
    setShowModal(false);
  };

  const submitFormHandler = e => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (err) return;
      setFormLoading(true);

      const contactName = values[FIELD_NAME.CONTACT_PERSON];
      const contactPhone = values[FIELD_NAME.CONTACT_NUMBER];
      const addressDetail = values[FIELD_NAME.ADDRESS_DETAIL];
      const locationNotes = values[FIELD_NAME.LOCATION_NOTES];
      const grabPoiStringified = values[FIELD_NAME.ADDRESS];
      const grabPoi = grabPoiStringified ? JSON.parse(grabPoiStringified) : {};

      const requestParams = {
        shop: shopIdentifier,
        shopifyPickUpAddress: {
          shopifyLocationID,
          poiID: grabPoi.poiID,
          grabPoi: {
            poiID: grabPoi.poiID,
            address: grabPoi.address,
            keywords: grabPoi.keywords,
            coordinates: grabPoi.coordinates || grabPoi.latlng,
            country: grabPoi.country,
            countryID: grabPoi.countryID,
            city: grabPoi.city,
            cityID: grabPoi.cityID,
          },
          metaData: {
            contact: {
              name: contactName,
              phone: contactPhone,
            },
            addressDetail,
            locationNotes,
          },
        },
      };

      fireSavePickUpAddressTrackEvent(
        defaultFormInitialValue.grabPoi.poiID,
        requestParams.shopifyPickUpAddress.poiID,
      );

      updateShopifyConfigurationPickUpAddress(requestParams, companyID)
        .then(success => {
          if (!success) {
            showNotification(makeErrorMessage());
            return;
          }
          setFormLoading(false);
          setShowModal(false);
          refetchShops();
        })
        .catch(async err => {
          const { message } = await err.json();
          showNotification(makeErrorMessage(message));
        })
        .finally(() => {
          setFormLoading(false);
        });
    });
  };

  const ctxValue = React.useMemo(
    () => ({
      form,
      formId,
      userInfo,
      refetchShops,
      pickUpAddress,
      showModal,
      setShowModal,
      isFormLoading,
      setFormLoading,
      submitFormHandler,
      cancelFormHandler,
      formInitialValues,
    }),
    [
      form,
      formId,
      userInfo,
      refetchShops,
      pickUpAddress,
      showModal,
      setShowModal,
      isFormLoading,
      setFormLoading,
      submitFormHandler,
      cancelFormHandler,
      formInitialValues,
    ],
  );

  return (
    <ShopAddressSettingsModalContext.Provider value={ctxValue}>
      {children}
    </ShopAddressSettingsModalContext.Provider>
  );
};

ShopAddressSettingsModalContextProvider.propTypes = {
  children: PropTypes.element.isRequired,
  dispatch: PropTypes.func.isRequired,
  form: PropTypes.shape({}).isRequired,
  pickUpAddress: PropTypes.shape({}).isRequired,
};

export default connect(null)(
  Form.create()(ShopAddressSettingsModalContextProvider),
);
